import { useEffect, useRef, useCallback, useState } from 'react';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor, editorConfig } from './ckeditor-custom-build';

import normalizeHtmlForCKEditor from '@shared/utils/normalizeHtmlForCKEditor';

import styles from './CKEditorWrapper.module.scss';

/**
 * CKEditorWrapper 컴포넌트
 * - CKEditor 5를 React에서 사용하기 위한 래퍼 컴포넌트
 * - 무료 오픈소스 버전 사용 (GPL/LGPL/MPL)
 * - DOM 초기화 오류 없이 안전하게 사용
 */

export default function CKEditorWrapper({
  value,
  onChange,
  placeholder,
  wrapperClassName,
  showPasteHelpHint = false,
}) {
  const editorRef = useRef(null);
  const addPageBreaksRef = useRef(null);
  const pasteHelpHintRef = useRef(null);
  const [error, setError] = useState(null);

  // 플래그: setData 호출 중인지 추적 (무한 루프 방지)
  const isSettingDataRef = useRef(false);
  // 플래그: 에디터가 준비되었는지
  const isEditorReadyRef = useRef(false);
  // 이전 value를 저장 (외부에서 변경 감지용)
  const lastExternalValueRef = useRef(value);

  // A4 페이지 구분선 추가 함수
  const addPageBreaks = useCallback(function addPageBreaksCallback() {
    if (!editorRef.current) return;

    try {
      const editor = editorRef.current;
      const viewRoot = editor.editing.view.document.getRoot();
      const editableElement = editor.editing.view.domRoots.get(viewRoot);

      if (!editableElement) return;

      // 기존 페이지 구분선 제거
      const existingBreaks = editableElement.querySelectorAll('.page-break-line');
      existingBreaks.forEach(function (breakEl) {
        breakEl.remove();
      });

      // A4 세로 크기를 픽셀로 변환 (297mm)
      const a4HeightPx = 297 * 3.7795275590551;
      const contentHeight = editableElement.scrollHeight;
      const pageCount = Math.ceil(contentHeight / a4HeightPx);

      for (let i = 2; i <= pageCount; i++) {
        const breakLine = document.createElement('div');
        breakLine.className = 'page-break-line';
        breakLine.style.cssText =
          'position: absolute; left: 0; width: 100%; height: 2px; ' +
          'background: linear-gradient(to right, transparent 0%, #c0c0c0 5%, #c0c0c0 95%, transparent 100%); ' +
          'pointer-events: none; z-index: 1000; top: ' +
          (i - 1) * a4HeightPx +
          'px;';

        const label = document.createElement('span');
        label.textContent = '다음 페이지';
        label.style.cssText =
          'position: absolute; left: 50%; transform: translateX(-50%); ' +
          'background: #fff; padding: 2px 10px; color: #999; font-size: 11px; white-space: nowrap;';
        breakLine.appendChild(label);
        editableElement.appendChild(breakLine);
      }
    } catch (e) {
      console.warn('페이지 구분선 추가 중 오류:', e);
    }
  }, []);

  // 에디터 준비 완료 시
  const handleReady = useCallback(
    function handleReadyCallback(editor) {
      try {
        editorRef.current = editor;
        isEditorReadyRef.current = true;
        setError(null);

        // 에디터 DOM 요소에 스타일 설정
        const viewRoot = editor.editing.view.document.getRoot();
        const domRoot = editor.editing.view.domRoots.get(viewRoot);

        if (domRoot) {
          domRoot.style.position = 'relative';
        }

        // ? 아이콘을 툴바 마지막 줄 세로 중앙에 맞춰 배치
        if (showPasteHelpHint && pasteHelpHintRef.current) {
          var editorElement = editor.ui.view.element;
          var toolbarEl = editorElement && editorElement.querySelector('.ck-toolbar');
          if (toolbarEl) {
            var lastRowBtnAll = toolbarEl.querySelectorAll('.ck-toolbar__items > *');
            var lastBtn = lastRowBtnAll.length > 0 ? lastRowBtnAll[lastRowBtnAll.length - 1] : null;
            var btnHeight = lastBtn ? lastBtn.offsetHeight : 28;
            var btnTop = lastBtn ? lastBtn.offsetTop : 0;
            var iconSize = 18;
            var topPos = btnTop + Math.round((btnHeight - iconSize) / 2);
            pasteHelpHintRef.current.style.top = topPos + 'px';
          }
        }

        // 초기 데이터 설정 (value가 있으면)
        if (value && value !== '') {
          isSettingDataRef.current = true;
          lastExternalValueRef.current = value;
          // font-size 유지하여 양식의 폰트 크기 보존
          const normalizedValue = normalizeHtmlForCKEditor(value, {
            removeFontSize: false,
            stripNegativeTextIndent: true,
          });
          editor.setData(normalizedValue);

          setTimeout(function () {
            isSettingDataRef.current = false;
            addPageBreaks();
            addPageBreaksRef.current = addPageBreaks;
          }, 100);
        } else {
          setTimeout(function () {
            addPageBreaks();
            addPageBreaksRef.current = addPageBreaks;
          }, 200);
        }

        // 에디터 데이터 변경 이벤트 (페이지 구분선 업데이트용)
        editor.editing.view.document.on('change:data', function () {
          setTimeout(function () {
            if (addPageBreaksRef.current) {
              addPageBreaksRef.current();
            }
          }, 500);
        });
      } catch (e) {
        console.error('CKEditor 초기화 오류:', e);
        setError('에디터를 초기화하는 중 오류가 발생했습니다.');
      }
    },
    [value, addPageBreaks, showPasteHelpHint],
  );

  // 에디터 내용 변경 시 (사용자 입력)
  const handleChange = useCallback(
    function handleChangeCallback(event, editor) {
      // setData로 인한 변경이면 무시 (무한 루프 방지)
      if (isSettingDataRef.current) {
        return;
      }

      const data = editor.getData();
      // 외부 value와 동기화
      lastExternalValueRef.current = data;

      if (onChange) {
        onChange(data);
      }

      // 페이지 구분선 업데이트
      setTimeout(function () {
        if (addPageBreaksRef.current) {
          addPageBreaksRef.current();
        }
      }, 500);
    },
    [onChange],
  );

  // value prop이 외부에서 변경될 때 에디터 내용 업데이트
  useEffect(
    function valueChangeEffect() {
      // 에디터가 준비되지 않았으면 무시 (handleReady에서 처리)
      if (!isEditorReadyRef.current || !editorRef.current) {
        return;
      }

      // setData 호출 중이면 무시
      if (isSettingDataRef.current) {
        return;
      }

      // 외부에서 value가 변경되었는지 확인
      if (value !== lastExternalValueRef.current && value !== undefined) {
        isSettingDataRef.current = true;
        lastExternalValueRef.current = value;

        const normalizedValue = normalizeHtmlForCKEditor(value || '', {
          removeFontSize: false,
          stripNegativeTextIndent: true,
        });
        editorRef.current.setData(normalizedValue);

        setTimeout(function () {
          isSettingDataRef.current = false;
          if (addPageBreaksRef.current) {
            addPageBreaksRef.current();
          }
        }, 200);
      }
    },
    [value],
  );

  // 컴포넌트 언마운트 시 에디터 정리
  useEffect(function cleanupEffect() {
    return function cleanup() {
      if (editorRef.current) {
        try {
          editorRef.current.destroy().catch(function (err) {
            console.warn('CKEditor 정리 중 오류:', err);
          });
          editorRef.current = null;
          isEditorReadyRef.current = false;
        } catch (e) {
          console.warn('CKEditor 정리 중 오류:', e);
        }
      }
    };
  }, []);

  // 에러 핸들러
  const handleError = useCallback(function handleErrorCallback(errorInfo, details) {
    console.error('CKEditor 오류:', errorInfo);
    setError('에디터 오류: ' + (errorInfo.message || '알 수 없는 오류'));

    if (details.willEditorRestart) {
      editorRef.current = null;
      isEditorReadyRef.current = false;
    }
  }, []);

  const wrapperClass = [styles.editorWrapper, wrapperClassName].filter(Boolean).join(' ');

  // 에러가 있으면 에러 메시지 표시
  if (error) {
    return (
      <div className={wrapperClass}>
        <div
          style={{ padding: '20px', color: 'red', border: '1px solid red', borderRadius: '4px' }}
        >
          <strong>에디터 오류:</strong> {error}
          <br />
          <button
            type="button"
            onClick={function () {
              setError(null);
              window.location.reload();
            }}
            style={{ marginTop: '10px', padding: '5px 10px' }}
          >
            새로고침
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {showPasteHelpHint && (
        <div ref={pasteHelpHintRef} className={styles.pasteHelpHint}>
          <span className={styles.pasteHelpIcon}>?</span>
          <div className={styles.pasteHelpTooltip}>
            Word/한글 등에서 복사한 콘텐츠는 이미지가 자동으로 포함되지 않거나 정상적으로 표시되지
            않을 수 있습니다.
            <br />
            이미지가 깨져 보일 경우, 해당 이미지만 개별적으로 복사하여 다시 붙여넣기 해주세요.
          </div>
        </div>
      )}
      <CKEditor
        editor={ClassicEditor}
        data=""
        onReady={handleReady}
        onChange={handleChange}
        onError={handleError}
        config={{
          ...editorConfig,
          placeholder: placeholder || '내용을 입력하세요...',
        }}
      />
    </div>
  );
}
